import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { userManager } from "../lib/oidc";

/**
 * Recovers from a failed silent token renewal.
 *
 * oidc-client-ts fires `automaticSilentRenew` once, ~60s before the access token
 * expires, and gives up after a single failure (it only retries on a *timeout*).
 * react-oidc-context turns that failure into `auth.error` but deliberately keeps
 * `isAuthenticated` true, so nothing in the app noticed: it kept issuing requests
 * with a token that was about to die, every one of them 401'd, and
 * SubscriptionStatusCheck eventually ended the session — minutes after the real
 * problem, and with no way to tell an expired token from a network blip.
 *
 * So: retry with exponential backoff, and only once the retries are exhausted
 * fall back to a full redirect, which is near-invisible while the ZITADEL SSO
 * session is still alive.
 */
const MAX_ATTEMPTS = 4;
const BASE_DELAY_MS = 2_000;
const MAX_DELAY_MS = 30_000;

export default function TokenRenewalGuard() {
  const auth = useAuth();
  const renewFailed = auth.error?.source === "renewSilent";
  const activeNavigator = auth.activeNavigator;
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // A successful renewal dispatches USER_LOADED, which clears `auth.error`.
    if (!renewFailed) {
      if (attempt !== 0) setAttempt(0);
      return;
    }
    // An OIDC navigation is in flight (e.g. the user hit "log out"); leave it be.
    if (activeNavigator) return;

    let cancelled = false;
    const delay = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          // Not auth.signinSilent(): that wrapper flips `isLoading`, flashing
          // ProtectedRoute's full-screen spinner over a working app. The
          // UserManager still emits `userLoaded`, so AuthProvider stays in sync.
          await userManager.signinSilent();
        } catch {
          if (cancelled) return;
          if (attempt + 1 < MAX_ATTEMPTS) {
            setAttempt(attempt + 1);
            return;
          }
          // Out of silent options. Re-authenticate rather than let the app rot
          // on a dead token until the next request happens to 401.
          try {
            await userManager.signinRedirect();
          } catch {
            // Nothing left to try; the 401 path will surface it.
          }
        }
      })();
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [renewFailed, activeNavigator, attempt]);

  return null;
}
